import {Megaphone, ShieldAlert} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ColonyAvatar from '@/components/ColonyAvatar'
import ColonyOfficialStar from '@/components/ColonyOfficialStar'
import Layout from '@/components/Layout'
import PostThread from '@/components/PostThread'
import SafePostText from '@/components/SafePostText'
import SeoHead from '@/components/SeoHead'
import { colonyProfilePath } from '@/lib/colonyProfile'
import { formatRelativeTime } from '@/lib/format'
import { colonyDisplayName, listWalletProfiles, onWalletProfileUpdated, type WalletProfileMap } from '@/lib/walletProfiles'
import { listColonyFeed, onWalletPostCreated, onWalletPostDeleted, type WalletPost } from '@/lib/walletPosts'

function ColonyNewsfeed() {
  const [posts, setPosts] = useState<WalletPost[]>([])
  const [avatars, setAvatars] = useState<WalletProfileMap>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    listColonyFeed(50).then((list) => {
      if (active)
        setPosts(list)
    }).finally(() => {
      if (active)
        setLoading(false)
    })
    return () => { active = false }
  }, [])

  const posterWallets = posts.map(post => post.wallet).filter(Boolean).join(',')

  useEffect(() => {
    if (!posterWallets)
      return
    const wallets = posterWallets.split(',')
    let active = true
    listWalletProfiles(wallets).then((map) => {
      if (active)
        setAvatars(prev => ({ ...prev, ...map }))
    })
    return () => { active = false }
  }, [posterWallets])

  useEffect(() => {
    return onWalletPostCreated((post) => {
      setPosts(prev => [post, ...prev.filter(item => item.id !== post.id)].slice(0, 50))
    })
  }, [])

  useEffect(() => {
    return onWalletPostDeleted((postId) => {
      setPosts(prev => prev.filter(item => item.id !== postId))
    })
  }, [])

  useEffect(() => {
    return onWalletProfileUpdated((profile) => {
      const key = profile.wallet?.toLowerCase()
      if (!key)
        return
      setAvatars(prev => ({
        ...prev,
        [key]: {
          image: profile.image || prev[key]?.image || '',
          username: profile.username ?? prev[key]?.username ?? '',
        },
      }))
    })
  }, [])

  return (
    <Layout>
      <SeoHead
        title="Colony Newsfeed"
        description="Read public Penguin Pad Colony posts from members — usernames, photos, and captions on Robinhood Chain."
        url="https://penguinpad.xyz/colony-newsfeed"
      />

      <section className="mx-auto max-w-3xl px-4 pb-16 pt-12 sm:px-6 lg:px-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Community</p>
        <div className="mt-2 flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-lime-400" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-white sm:text-4xl">Colony Newsfeed</h1>
        </div>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">
          Latest posts from Colony members. Each post shows the author’s username and profile photo, plus any image and text they shared on their profile.
        </p>
        <p className="mt-3 text-xs">
          <Link to="/explore-colony" className="font-medium text-lime-400 hover:text-lime-300">Explore the Colony</Link>
          <span className="mx-2 text-zinc-600">·</span>
          <Link to="/my-colony" className="font-medium text-zinc-300 hover:text-white">Post from My Colony</Link>
        </p>
        <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden="true" />
          Do not trust outside links in captions. Only official penguinpad.xyz project links appear in lime green and open the project.
        </p>

        {loading
          ? <p className="mt-8 text-sm text-zinc-500">Loading Colony posts…</p>
          : posts.length > 0
            ? (
                <ul className="mt-8 space-y-4">
                  {posts.map((post) => {
                    const profile = avatars[post.wallet?.toLowerCase()]
                    const name = colonyDisplayName(post.wallet, profile?.username)
                    return (
                      <li key={post.id} className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
                        <div className="flex items-start gap-3">
                          <Link
                            to={colonyProfilePath(post.wallet)}
                            aria-label={`Open ${name} Colony profile`}
                            className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60"
                          >
                            <ColonyAvatar wallet={post.wallet} image={profile?.image} size="md" anonymous />
                          </Link>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                              <Link
                                to={colonyProfilePath(post.wallet)}
                                className="inline-flex max-w-full items-center gap-1 truncate text-sm font-semibold text-white hover:text-lime-300"
                              >
                                <span className="truncate">{name}</span>
                                <ColonyOfficialStar wallet={post.wallet} size="sm" />
                              </Link>
                              <span className="text-[11px] text-zinc-500">{formatRelativeTime(post.createdAt)}</span>
                            </div>
                            <SafePostText text={post.caption} extraLink={post.link} className="mt-2" />
                            <PostThread postKind="wallet" postId={post.id} image={post.image} imageAlt={`${name} Colony post photo`} />
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )
            : (
                <div className="mt-10 rounded-3xl border border-dashed border-white/15 px-4 py-12 text-center">
                  <Megaphone className="mx-auto h-7 w-7 text-zinc-600" aria-hidden="true" />
                  <p className="mt-3 text-sm text-zinc-400">No Colony posts yet. Share a note or photo on your public profile.</p>
                  <Link to="/explore-colony" className="mt-4 inline-flex text-sm font-medium text-lime-400 hover:text-lime-300">
                    Explore the Colony
                  </Link>
                </div>
              )}
      </section>
    </Layout>
  )
}

export default ColonyNewsfeed
